import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LogOut, Bell, Calendar, CheckCircle, Clock, MapPin, 
  User, Phone, ChevronRight, Briefcase, Settings, Loader2,
  Check, X, MessageSquare, PoundSterling, AlertTriangle,
  Upload, FileText, Receipt
} from "lucide-react";
import { API } from "@/App";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  quote_pending: "bg-orange-100 text-orange-700",
  quote_required: "bg-purple-100 text-purple-700",
  accepted: "bg-blue-100 text-blue-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  en_route: "bg-purple-100 text-purple-700",
  on_site: "bg-green-100 text-green-700",
  completed: "bg-emerald-100 text-emerald-700",
  no_show: "bg-red-100 text-red-700"
};

export const ContractorDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [diaryAvailable, setDiaryAvailable] = useState(true);
  const [togglingDiary, setTogglingDiary] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  
  // No-show modal state
  const [noShowModal, setNoShowModal] = useState(false);
  const [noShowJob, setNoShowJob] = useState(null);
  const [noShowReason, setNoShowReason] = useState("");
  const [reportingNoShow, setReportingNoShow] = useState(false);
  
  // Quote modal state
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedJobForQuote, setSelectedJobForQuote] = useState(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteDescription, setQuoteDescription] = useState("");
  const [submittingQuote, setSubmittingQuote] = useState(false);

  // Invoice upload modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedJobForInvoice, setSelectedJobForInvoice] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/contractor-management/insurance/status`, { headers });
      setOnboardingStatus(response.data);
      
      const status = response.data.status;
      // Redirect to onboarding if not active
      if (status && status !== "active") {
        // Redirect all non-active statuses to onboarding
        navigate("/contractor/onboard");
        return;
      }
      
      // Only fetch data if contractor is active
      fetchData();
    } catch (error) {
      // Check for 403 - contractor status issue
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || "";
        if (detail.includes("CONTRACTOR_")) {
          navigate("/contractor/onboard");
          return;
        }
      }
      // If endpoint fails, contractor might be legacy (no status) - continue normally
      console.log("Onboarding check:", error.response?.data?.detail || "No status found");
      fetchData();
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [availableRes, jobsRes, diaryRes] = await Promise.all([
        axios.get(`${API}/maintenance/contractor/pending-jobs`, { headers }),
        axios.get(`${API}/jobs`, { headers }),
        axios.get(`${API}/contractors/diary-status`, { headers }).catch(() => ({ data: { diary_available: true }}))
      ]);

      setAvailableJobs(availableRes.data.pending_jobs || []);
      setMyJobs(jobsRes.data || []);
      setDiaryAvailable(diaryRes.data.diary_available ?? true);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
      }
      // If 403 due to contractor status, redirect to onboarding
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || "";
        if (detail.includes("CONTRACTOR_")) {
          toast.error("Access denied. Please complete onboarding.");
          navigate("/contractor/onboard");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDiary = async () => {
    setTogglingDiary(true);
    try {
      const response = await axios.patch(
        `${API}/contractors/diary-availability`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiaryAvailable(response.data.diary_available);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to update diary availability");
    } finally {
      setTogglingDiary(false);
    }
  };

  const handleAcceptJob = async (jobId) => {
    setActionLoading(jobId);
    try {
      const response = await axios.post(
        `${API}/maintenance/jobs/${jobId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.is_emergency) {
        toast.success("Emergency job accepted! You can proceed immediately.");
      } else {
        toast.success("Job accepted! Please submit a quote for landlord approval.");
      }
      
      fetchData();
      
      // If not emergency, open quote modal
      if (response.data.needs_quote) {
        const job = availableJobs.find(j => j.job_id === jobId);
        setSelectedJobForQuote(job || { job_id: jobId });
        setQuoteModalOpen(true);
      }
    } catch (error) {
      toast.error("Failed to accept job");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitQuote = async () => {
    if (!quoteAmount || parseFloat(quoteAmount) <= 0) {
      toast.error("Please enter a valid quote amount");
      return;
    }
    
    setSubmittingQuote(true);
    try {
      await axios.post(
        `${API}/maintenance/jobs/${selectedJobForQuote.job_id}/quote`,
        {
          amount: parseFloat(quoteAmount),
          description: quoteDescription
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Quote submitted! Waiting for landlord approval.");
      setQuoteModalOpen(false);
      setQuoteAmount("");
      setQuoteDescription("");
      setSelectedJobForQuote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to submit quote");
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Invoice upload functions
  const openInvoiceModal = (job) => {
    setSelectedJobForInvoice(job);
    setInvoiceFile(null);
    setInvoiceAmount(job.quote_contractor_price?.toString() || job.quote_amount?.toString() || "");
    setInvoiceNotes("");
    setInvoiceModalOpen(true);
  };

  const handleUploadInvoice = async () => {
    if (!invoiceFile) {
      toast.error("Please select an invoice file");
      return;
    }
    if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
      toast.error("Please enter the invoice amount");
      return;
    }

    setUploadingInvoice(true);
    try {
      const formData = new FormData();
      formData.append("file", invoiceFile);
      formData.append("invoice_amount", invoiceAmount);
      if (invoiceNotes) formData.append("notes", invoiceNotes);

      await axios.post(
        `${API}/contractor/upload-invoice/${selectedJobForInvoice.job_id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );

      toast.success("Invoice uploaded successfully!");
      setInvoiceModalOpen(false);
      setSelectedJobForInvoice(null);
      setInvoiceFile(null);
      setInvoiceAmount("");
      setInvoiceNotes("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload invoice");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const openQuoteModal = (job) => {
    setSelectedJobForQuote(job);
    setQuoteModalOpen(true);
  };

  const handleUpdateStatus = async (jobId, status) => {
    setActionLoading(jobId);
    try {
      await axios.patch(
        `${API}/jobs/${jobId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status updated!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setActionLoading(null);
    }
  };

  const openNoShowModal = (job) => {
    setNoShowJob(job);
    setNoShowReason("");
    setNoShowModal(true);
  };

  const handleReportNoShow = async () => {
    if (!noShowJob) return;
    
    setReportingNoShow(true);
    try {
      await axios.post(
        `${API}/jobs/${noShowJob.job_id}/report-no-show`,
        { reason: noShowReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("No-show reported. A cancellation fee will be applied.");
      setNoShowModal(false);
      setNoShowJob(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to report no-show");
    } finally {
      setReportingNoShow(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeJobs = myJobs.filter(j => !["completed", "declined"].includes(j.status));
  const completedJobs = myJobs.filter(j => j.status === "completed");

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Welcome back,</p>
            <h1 className="text-xl font-semibold font-outfit">{user?.name || "Contractor"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/contractor/messages")}
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/contractor/invoices")}
              data-testid="invoices-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Invoices
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/contractor/availability")}
              data-testid="availability-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Availability
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Diary Toggle */}
        <div className="max-w-4xl mx-auto px-4 pb-3">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
            <div className="flex items-center gap-3">
              <Calendar className={`w-5 h-5 ${diaryAvailable ? 'text-green-600' : 'text-slate-400'}`} />
              <div>
                <p className="font-medium text-sm">Diary Available</p>
                <p className="text-xs text-muted-foreground">
                  {diaryAvailable ? "Tenants can book appointments" : "Booking is disabled"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {togglingDiary && <Loader2 className="w-4 h-4 animate-spin" />}
              <Switch
                checked={diaryAvailable}
                onCheckedChange={handleToggleDiary}
                disabled={togglingDiary}
                data-testid="diary-toggle"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">{availableJobs.length}</p>
              <p className="text-sm text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{activeJobs.length}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{completedJobs.length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="available" className="space-y-4">
          <TabsList className="w-full">
            <TabsTrigger value="available" className="flex-1" data-testid="tab-available">
              Available ({availableJobs.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="flex-1" data-testid="tab-active">
              Active ({activeJobs.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex-1" data-testid="tab-completed">
              Completed ({completedJobs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="space-y-3">
            {availableJobs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No available jobs</h3>
                  <p className="text-sm text-muted-foreground">
                    Check back later for new jobs in your area
                  </p>
                </CardContent>
              </Card>
            ) : (
              availableJobs.map((job) => (
                <Card key={job.job_id} className="job-card" data-testid={`job-${job.job_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {job.is_emergency ? (
                            <Badge className="bg-red-100 text-red-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Emergency
                            </Badge>
                          ) : (
                            <Badge variant="secondary">New</Badge>
                          )}
                          <span className="text-sm text-muted-foreground">
                            Job #{job.job_id?.slice(-6)}
                          </span>
                        </div>
                        <p className="font-medium mb-1">Issue: {job.issue_id?.slice(-6)}</p>
                        <p className="text-sm text-muted-foreground">
                          Posted {new Date(job.created_at).toLocaleDateString()}
                        </p>
                        {job.is_emergency && (
                          <p className="text-sm text-red-600 mt-1">
                            ⚡ No quote needed - proceed immediately
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={() => handleAcceptJob(job.job_id)}
                        disabled={actionLoading === job.job_id}
                        data-testid={`accept-job-${job.job_id}`}
                      >
                        {actionLoading === job.job_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Accept
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-3">
            {activeJobs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No active jobs</h3>
                  <p className="text-sm text-muted-foreground">
                    Accept a job to get started
                  </p>
                </CardContent>
              </Card>
            ) : (
              activeJobs.map((job) => (
                <Card key={job.job_id} className="job-card" data-testid={`my-job-${job.job_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={statusColors[job.status] || "bg-slate-100"}>
                            {job.status?.replace(/_/g, " ")}
                          </Badge>
                          {job.is_emergency && (
                            <Badge className="bg-red-100 text-red-700">Emergency</Badge>
                          )}
                        </div>
                        <p className="font-medium">Job #{job.job_id?.slice(-6)}</p>
                        {/* Show contractor's original quote, not the marked-up price */}
                        {(job.quote_contractor_price || job.quote_amount) && (
                          <p className="text-sm text-muted-foreground">
                            Your quote: £{(job.quote_contractor_price || job.quote_amount).toFixed(2)}
                            {job.quote_approved && <span className="text-green-600 ml-2">✓ Approved</span>}
                          </p>
                        )}
                        {job.scheduled_time && (
                          <p className="text-sm text-primary mt-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {new Date(job.scheduled_time).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action buttons based on status */}
                    <div className="flex flex-wrap gap-2">
                      {/* Quote pending - show submit quote button */}
                      {job.status === "quote_pending" && (
                        <Button
                          size="sm"
                          onClick={() => openQuoteModal(job)}
                          data-testid={`submit-quote-${job.job_id}`}
                        >
                          <PoundSterling className="w-4 h-4 mr-1" />
                          Submit Quote
                        </Button>
                      )}
                      
                      {/* Quote required/waiting for approval */}
                      {job.status === "quote_required" && (
                        <Badge className="bg-amber-100 text-amber-700 py-2">
                          <Clock className="w-3 h-3 mr-1" />
                          Awaiting Landlord Approval
                        </Badge>
                      )}
                      
                      {/* Accepted - can schedule */}
                      {job.status === "accepted" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(job.job_id, "scheduled")}
                          disabled={actionLoading === job.job_id}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Schedule
                        </Button>
                      )}
                      
                      {/* Scheduled - can go en route or report no-show */}
                      {job.status === "scheduled" && (
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateStatus(job.job_id, "en_route")}
                            disabled={actionLoading === job.job_id}
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            On My Way
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={() => openNoShowModal(job)}
                            disabled={actionLoading === job.job_id}
                            data-testid={`no-show-${job.job_id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            No Access
                          </Button>
                        </div>
                      )}
                      
                      {/* En route - can arrive */}
                      {job.status === "en_route" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUpdateStatus(job.job_id, "on_site")}
                          disabled={actionLoading === job.job_id}
                        >
                          <User className="w-4 h-4 mr-1" />
                          Arrived
                        </Button>
                      )}
                      
                      {/* On site - can complete */}
                      {job.status === "on_site" && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(job.job_id, "completed")}
                          disabled={actionLoading === job.job_id}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      
                      <Button size="sm" variant="ghost">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Message
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Completed Jobs Tab */}
          <TabsContent value="completed" className="space-y-3">
            {completedJobs.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No completed jobs</h3>
                  <p className="text-sm text-muted-foreground">
                    Jobs you complete will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              completedJobs.map((job) => (
                <Card key={job.job_id} className="job-card" data-testid={`completed-job-${job.job_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                          {job.contractor_invoice_file && (
                            <Badge className="bg-blue-100 text-blue-700">
                              <Receipt className="w-3 h-3 mr-1" />
                              Invoiced
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium">Job #{job.job_id?.slice(-6)}</p>
                        {job.issue?.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {job.issue.description}
                          </p>
                        )}
                        {(job.quote_contractor_price || job.quote_amount) && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Your quote: £{(job.quote_contractor_price || job.quote_amount).toFixed(2)}
                          </p>
                        )}
                        {job.completed_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Completed: {new Date(job.completed_at || job.updated_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Invoice status & action */}
                    <div className="flex items-center justify-between pt-3 border-t">
                      {job.contractor_invoice_file ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <FileText className="w-4 h-4 text-green-600" />
                          <span>
                            Invoice: £{job.contractor_invoice_amount?.toFixed(2)}
                            {job.contractor_payment_status === "paid" && (
                              <Badge className="ml-2 bg-green-100 text-green-700">Paid</Badge>
                            )}
                            {job.contractor_payment_status === "invoiced" && (
                              <Badge className="ml-2 bg-amber-100 text-amber-700">Pending Payment</Badge>
                            )}
                          </span>
                        </div>
                      ) : (
                        <p className="text-sm text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Invoice not uploaded
                        </p>
                      )}
                      
                      {!job.contractor_invoice_file && (
                        <Button 
                          size="sm" 
                          onClick={() => openInvoiceModal(job)}
                          data-testid={`upload-invoice-${job.job_id}`}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Upload Invoice
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Bottom Nav */}
      <nav className="bottom-nav">
        <div className="max-w-4xl mx-auto flex items-center justify-around">
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor")}
            data-testid="nav-jobs"
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-xs">Jobs</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor/availability")}
            data-testid="nav-diary"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Diary</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor/messages")}
            data-testid="nav-messages"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-xs">Messages</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor/settings")}
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>

      {/* Quote Submission Modal */}
      <Dialog open={quoteModalOpen} onOpenChange={setQuoteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5" />
              Submit Quote
            </DialogTitle>
            <DialogDescription>
              Enter your quote for this job. The landlord will need to approve it before you can proceed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="amount">Quote Amount (£) *</Label>
              <div className="relative mt-1">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={quoteAmount}
                  onChange={(e) => setQuoteAmount(e.target.value)}
                  className="pl-9"
                  data-testid="quote-amount-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Breakdown of costs, parts needed, etc."
                value={quoteDescription}
                onChange={(e) => setQuoteDescription(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="quote-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitQuote} 
              disabled={submittingQuote || !quoteAmount}
              data-testid="submit-quote-btn"
            >
              {submittingQuote ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Submit Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* No-Show Modal */}
      <Dialog open={noShowModal} onOpenChange={setNoShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Report No Access / No-Show
            </DialogTitle>
            <DialogDescription>
              Report that you were unable to gain access to the property. A cancellation fee of £150 (inc VAT) will be charged to the landlord.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {noShowJob && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Job Reference</p>
                <p className="font-semibold">#{noShowJob.job_id?.slice(-8).toUpperCase()}</p>
                <p className="text-sm mt-1">{noShowJob.issue_type}</p>
                {noShowJob.scheduled_time && (
                  <p className="text-sm text-muted-foreground">
                    Scheduled: {new Date(noShowJob.scheduled_time).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> Only report a no-show if you arrived at the scheduled time and were unable to access the property. This will result in a cancellation fee being charged.
              </p>
            </div>
            
            <div>
              <Label htmlFor="reason">Reason (optional)</Label>
              <Textarea
                id="reason"
                placeholder="e.g., No one answered the door, key safe code didn't work..."
                value={noShowReason}
                onChange={(e) => setNoShowReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="no-show-reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReportNoShow} 
              disabled={reportingNoShow}
              data-testid="confirm-no-show-btn"
            >
              {reportingNoShow ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              Report No-Show
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Modal */}
      <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Upload Invoice
            </DialogTitle>
            <DialogDescription>
              Upload your invoice for this completed job. You'll receive payment once the invoice is processed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedJobForInvoice && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Job Reference</p>
                <p className="font-semibold">#{selectedJobForInvoice.job_id?.slice(-8).toUpperCase()}</p>
                {selectedJobForInvoice.issue_type && (
                  <p className="text-sm mt-1">{selectedJobForInvoice.issue_type}</p>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="invoiceFile">Invoice File (PDF) *</Label>
              <div className="mt-1">
                <Input
                  id="invoiceFile"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
                  data-testid="invoice-file-input"
                />
                {invoiceFile && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {invoiceFile.name}
                  </p>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="invoiceAmount">Invoice Amount (£) *</Label>
              <div className="relative mt-1">
                <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="invoiceAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  className="pl-9"
                  data-testid="invoice-amount-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="invoiceNotes">Notes (optional)</Label>
              <Textarea
                id="invoiceNotes"
                placeholder="Any additional details about this invoice..."
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="invoice-notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadInvoice} 
              disabled={uploadingInvoice || !invoiceFile || !invoiceAmount}
              data-testid="submit-invoice-btn"
            >
              {uploadingInvoice ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
